// ACCS-11 — Back with a dialog open closes the dialog and leaves the page where it
// is (TC-ACCS-11-01 step 4). Without this, the browser's Back navigated the page
// away underneath an open detail or confirmation.
//
// A router blocker stops Back and Forward (POP) while an AGE modal is open; the
// router puts the address back, and the topmost modal is closed the way AGE closes
// it on Escape, so the page's own `onModalDismiss` handler runs as for any other
// dismissal. A modal that refuses Escape stays open: Back is the same request.
// Pushes and replaces are never blocked, because a modal's own action may navigate.
//
// Rendered from router.tsx, so only the data router ever renders it: `useBlocker`
// throws under the plain MemoryRouter the shell's unit specs use. React Router
// honours one blocker at a time, so a later dirty-form guard composes into this one.
// per docs/shape-ops-readable-urls-and-history.md §4
import { useEffect } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';

/** The part of AGE's `mud-modal` element this guard drives. */
interface OpenModalElement extends HTMLElement {
  closeOnEscape?: boolean;
  closeModal?: (reason?: 'escape') => Promise<void>;
}

/** The modal a reader sees on top: the last open one in document order. */
export function topmostOpenModal(root: ParentNode = document): OpenModalElement | null {
  const open = root.querySelectorAll<OpenModalElement>('mud-modal[open]');
  return open.length > 0 ? (open[open.length - 1] ?? null) : null;
}

export const blockHistoryStepWhileModalOpen: BlockerFunction = ({ historyAction }) =>
  historyAction === 'POP' && topmostOpenModal() !== null;

export function BackClosesModal(): null {
  const blocker = useBlocker(blockHistoryStepWhileModalOpen);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const modal = topmostOpenModal();
    if (modal && modal.closeOnEscape !== false) void modal.closeModal?.('escape');
    blocker.reset();
  }, [blocker]);

  return null;
}
